/* eslint-disable jira/naming/disallow-filenames */

// eslint-disable-next-line jira/filename-case
import React, { useCallback, Fragment, memo, type PropsWithChildren } from 'react';
import { createRouterSelector } from '@atlassian/react-resource-router';
import { Placement as RovoNudgePlacement } from '@post-office/placements/rovo-nudge/frontend';
import { Placement as JsmLiveChatPlacement } from '@post-office/placements/jsm-live-chat-notifications/frontend';
import AppProvider from '@atlaskit/app-provider/app-provider';
import { token } from '@atlaskit/tokens';
import AnalyticsProviders from '@jira/platform__observability-analytics-providers/src/AnalyticsProviders.tsx';
import { ViewExperienceTrackingProvider } from '@jira/platform__experience-tracking-viewing/src/view/experience-tracking-provider/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import PerformanceMark from '@atlassian/jira-common-performance/src/set-performance-mark.tsx';
import EngagementProvider from '@jira/platform__observability-engagement/src/index.tsx';
import getMeta from '@jira/platform__utils-get-meta/src/utils/get-meta/index.tsx';
import { SidebarNav4ControllerProvider } from '@jira/navigation-apps__sidebar-nav4-context/src/controllers/sidebar-controller-context/index.tsx';
import {
	CONTAINER_EDITION,
	CONTAINER_EXPERIENCE_ID,
	CONTAINER_EXPERIENCE,
	CONTAINER_ANALYTICS_SOURCE,
	CONTAINER_APPLICATION,
	APP_BASE_CONTAINERS_END_MARK,
	APP_BASE_CONTAINERS_MARK,
	APP_BASE_CONTAINERS_START_MARK,
	ROUTER_CONTAINERS_END_MARK,
	ROUTER_CONTAINERS_MARK,
	ROUTER_CONTAINERS_START_MARK,
} from '@jira/platform__page-container-constants/src/index.tsx';
import { routeAnalyticsAttributes } from '@jira/platform__utils-route-analytics/src/main.tsx';
import { PostOfficeRouterListener } from '@jira/post-office__post-office-provider/src/index.tsx';
import { ScreenSpaceFlagsPlacement } from '@jira/post-office__post-office-provider/src/screen-space-flags-placement.tsx';
import JiraRelayEnvironmentProvider from '@jira/platform__graphql-relay-environment-provider/src/index.tsx';
import { useOrgId } from '@jira/router-resources__navigation-organization-id/src/useOrgId.tsx';
import { setInitialPageLoadTimingFromPerformanceMarks } from '@atlassian/jira-spa-performance-breakdown/src/utils/performance-marks-tools/index.tsx';
import { TenantContextContainer } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { PreviewPanelsProvider } from '@jira/issue__preview-panels/src/PreviewPanelsProvider.tsx';
import IssuePreviewViewModeProvider from '@jira/issue__issue-preview-view-mode/src/IssuePreviewViewModeProvider.tsx';
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component/src/index.tsx';
import { componentWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/component-with-condition/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { isWorkItemModernizationGatesEnabled } from '@jira/issue__eligibility/src/isWorkItemModernizationGatesEnabled.tsx';
import { ModalContextProvider } from '@atlassian/entry-points/modal-context-provider';
import { PanelProvider } from '@atlassian/panel-system';
import { UserPreferencesContainer } from '@jira/platform__services-user-preferences/src/components/main.tsx';
import { ErrorBoundary } from './error-boundary/main.tsx';
import { IntlProviders } from './intl-providers/main.tsx';
import { MetricsContainers } from './metrics-containers/main.tsx';
import { NavigationContainers } from './navigation-containers/main.tsx';
import { PageConfigContainers } from './page-config-containers/main.tsx';
import type { GlobalContainersProps } from './types.tsx';
import { UiManagers } from './ui-managers/main.tsx';
import { LinkingProviders } from './linking-providers/LinkingProviders.tsx';
import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';

const MaybePreviewPanelsProvider = componentWithFG(
	'jira-glance-panel',
	PreviewPanelsProvider,
	Fragment,
);

const EventHorizon = memo(({ children }: PropsWithChildren) => <>{children}</>);

const Passthrough = ({ children }: PropsWithChildren<{ name?: string; fallback?: unknown }>) => (
	<>{children}</>
);

const MaybeIssuePreviewViewModeProvider = componentWithCondition(
	() =>
		// eslint-disable-next-line jira/ff/no-preconditioning
		fg('jira_wiv_modal_view_consistency') && isWorkItemModernizationGatesEnabled(),
	IssuePreviewViewModeProvider,
	Passthrough,
);

// Deprioritises rendering child components when React progressive hydration is running.
const ScreenSpaceFlagsPlaceholder = componentWithFG(
	'jpx-463-spa-deprioritise-postoffice-screen-flags',
	Placeholder,
	Passthrough,
);

const MaybeRovoNudgePlacement = componentWithFG(
	'post-office_enable-rovo-nudge-placement_gate',
	RovoNudgePlacement,
	Fragment,
);

const MaybeJsmLiveChatPlacement = componentWithFG(
	'jsm_live_chat_browser_notifications',
	JsmLiveChatPlacement,
	Fragment,
);

export const useRouterLocationPathnameWithFG: () => string = functionWithCondition(
	() => fg('jira_screen_space_flag_route_change'),
	() => useRouterLocationPathname(),
	() => '',
);

const useRouterLocationPathname = createRouterSelector((state) => state.location.pathname);

/**
 * Remounts ScreenSpaceFlagsPlacement on every route/page URL change so that
 * Post Office can fetch and display the correct flags for the new page.
 *
 * Exported for testing only.
 */
export const ScreenSpaceFlagsPlacementWithRouteKey = (): React.JSX.Element => {
	const pathname = useRouterLocationPathnameWithFG();
	return (
		<ScreenSpaceFlagsPlaceholder name="screen-flags-placement">
			<ScreenSpaceFlagsPlacement
				{...(fg('jira_screen_space_flag_route_change') ? { key: pathname } : null)}
			/>
		</ScreenSpaceFlagsPlaceholder>
	);
};

/**
 * Serves as the foundational component orchestrating the integration of internationalization,
 * analytics, performance monitoring, and feature-flag based conditional rendering. It ensures a consistent
 * application structure and style setup, facilitating performance tracking and user experience improvements.
 */
export const GlobalContainers: React.MemoExoticComponent<
	({
		id,
		children,
		locale,
		messages,
		Router,
		routerLinkComponent,
	}: GlobalContainersProps) => React.JSX.Element
> = memo(
	({
		id,
		children,
		locale,
		messages,
		Router,
		routerLinkComponent,
	}: GlobalContainersProps): React.JSX.Element => {
		const globalContainersPerformanceTiming = useCallback(() => {
			setInitialPageLoadTimingFromPerformanceMarks(
				ROUTER_CONTAINERS_MARK,
				ROUTER_CONTAINERS_START_MARK,
				ROUTER_CONTAINERS_END_MARK,
				true,
				true,
			);
			setInitialPageLoadTimingFromPerformanceMarks(
				APP_BASE_CONTAINERS_MARK,
				APP_BASE_CONTAINERS_START_MARK,
				APP_BASE_CONTAINERS_END_MARK,
				true,
				true,
			);
		}, []);
		const { data: orgId } = useOrgId();
		// Unstable components cause bugs and performance problems - please fix this! go/unstable-components
		// eslint-disable-next-line react/no-unstable-nested-components
		const MonolithStyleOverride = () => (
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
			<style
				// eslint-disable-next-line react/no-danger
				dangerouslySetInnerHTML={{
					__html: `
                        body#jira {
                            overflow-x: hidden;
                            overflow-y: auto;
                        }

                        #jira-frontend {
                            background: ${token('elevation.surface')};
                        }

                        #jira_request_timing_info {
                            margin: 0;
                        }
                    `,
				}}
			/>
		);

		return (
			<IntlProviders messages={messages} locale={locale}>
				{__SPA__ && <MonolithStyleOverride />}
				<TenantContextContainer isGlobal getMeta={getMeta}>
					<AnalyticsProviders>
						<EngagementProvider orgId={orgId}>
							<ViewExperienceTrackingProvider
								analyticsSource={CONTAINER_ANALYTICS_SOURCE}
								experience={CONTAINER_EXPERIENCE}
								experienceId={CONTAINER_EXPERIENCE_ID}
								application={CONTAINER_APPLICATION}
								edition={CONTAINER_EDITION}
								additionalAttributes={routeAnalyticsAttributes}
							>
								<ErrorBoundary id={id} withExperienceTracker>
									<PerformanceMark metricKey={ROUTER_CONTAINERS_START_MARK} />
									<Router>
										<AppProvider UNSAFE_isThemingDisabled routerLinkComponent={routerLinkComponent}>
											<PerformanceMark metricKey={ROUTER_CONTAINERS_END_MARK} />
											<PageConfigContainers>
												<MetricsContainers>
													<JiraRelayEnvironmentProvider>
														<NavigationContainers>
															<UserPreferencesContainer isGlobal>
																<UiManagers>
																	<PerformanceMark metricKey={APP_BASE_CONTAINERS_START_MARK} />
																	<PostOfficeRouterListener>
																		<MaybePreviewPanelsProvider>
																			<MaybeIssuePreviewViewModeProvider>
																				<PanelProvider>
																					<LinkingProviders>
																						<ModalContextProvider>
																							<PerformanceMark
																								metricKey={APP_BASE_CONTAINERS_END_MARK}
																								onSet={globalContainersPerformanceTiming}
																							/>
																							<SidebarNav4ControllerProvider>
																								<EventHorizon>{children}</EventHorizon>
																							</SidebarNav4ControllerProvider>
																						</ModalContextProvider>
																					</LinkingProviders>
																				</PanelProvider>
																			</MaybeIssuePreviewViewModeProvider>
																		</MaybePreviewPanelsProvider>
																	</PostOfficeRouterListener>
																	<ScreenSpaceFlagsPlacementWithRouteKey />
																	<MaybeRovoNudgePlacement />
																	<MaybeJsmLiveChatPlacement />
																</UiManagers>
															</UserPreferencesContainer>
														</NavigationContainers>
													</JiraRelayEnvironmentProvider>
												</MetricsContainers>
											</PageConfigContainers>
										</AppProvider>
									</Router>
								</ErrorBoundary>
							</ViewExperienceTrackingProvider>
						</EngagementProvider>
					</AnalyticsProviders>
				</TenantContextContainer>
			</IntlProviders>
		);
	},
);
